import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Sparkles, MessageCircle, Shield, Phone, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import YouTubeCarousel from '../components/YouTubeCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';

const Landing = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [referralCode, setReferralCode] = useState(null);
  
  // WhatsApp Auth States
  const [showWhatsAppAuth, setShowWhatsAppAuth] = useState(false);
  const [authStep, setAuthStep] = useState('phone'); // 'phone' or 'otp'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Extract referral code from URL - support both 'ref' and 'referral_code' parameters
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('referral_code');
    if (ref) {
      setReferralCode(ref);
      // Store in localStorage for later use
      localStorage.setItem('referral_code', ref);
    }

    let isMounted = true;
    let timeoutId;
    
    const checkExistingAuth = async () => {
      try {
        // Set a timeout to stop checking after 5 seconds
        timeoutId = setTimeout(() => {
          if (isMounted && checking) {
            console.log('Auth check timeout - showing login');
            setChecking(false);
          }
        }, 5000);
        
        await axios.get(`${API}/auth/me`, { 
          withCredentials: true,
          timeout: 3000 
        });
        
        clearTimeout(timeoutId);
        
        if (isMounted) {
          navigate('/discover');
        }
      } catch (e) {
        clearTimeout(timeoutId);
        
        if (isMounted) {
          setChecking(false);
        }
      }
    };
    
    checkExistingAuth();
    
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navigate]);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleGoogleLogin = () => {
    // Include referral code in the redirect URL if present
    const storedRef = localStorage.getItem('referral_code');
    let redirectUrl = `${window.location.origin}/discover`;
    if (storedRef) {
      redirectUrl += `?referral_code=${storedRef}`;
    }
    window.location.href = `${AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/whatsapp/send-otp`, {
        phone_number: phoneNumber
      });

      if (response.data.success) {
        toast.success('OTP sent to your WhatsApp!');
        setAuthStep('otp');
        setCountdown(60); // 60 second countdown for resend
        
        // For testing: show debug OTP if available
        if (response.data.debug_otp) {
          console.log('Debug OTP:', response.data.debug_otp);
          toast.info(`Debug OTP: ${response.data.debug_otp}`, { duration: 10000 });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/whatsapp/verify-otp`, {
        phone_number: phoneNumber,
        otp: otp,
        remember_me: rememberMe
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success('Login successful!');
        
        // Redirect based on whether profile setup is needed
        if (response.data.needs_profile_setup) {
          navigate('/setup');
        } else {
          navigate('/discover');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/whatsapp/resend-otp`, {
        phone_number: phoneNumber
      });

      if (response.data.success) {
        toast.success('OTP resent!');
        setCountdown(60);
        
        if (response.data.debug_otp) {
          toast.info(`Debug OTP: ${response.data.debug_otp}`, { duration: 10000 });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const resetWhatsAppAuth = () => {
    setShowWhatsAppAuth(false);
    setAuthStep('phone');
    setPhoneNumber('');
    setOtp('');
    setCountdown(0);
  };

  if (checking) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  // WhatsApp Authentication Modal
  if (showWhatsAppAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
          {/* Back Button */}
          <button 
            onClick={resetWhatsAppAuth}
            className="flex items-center text-gray-500 hover:text-gray-700 mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">
              {authStep === 'phone' ? 'Sign in with WhatsApp' : 'Enter Verification Code'}
            </h2>
            <p className="text-gray-500 mt-2">
              {authStep === 'phone' 
                ? 'We\'ll send you a verification code via WhatsApp'
                : `Code sent to ${phoneNumber}`
              }
            </p>
          </div>

          {/* Phone Number Step */}
          {authStep === 'phone' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  placeholder="+1 234 567 8900"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full text-lg py-6"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Include country code (e.g., +1 for US, +592 for Guyana)
                </p>
              </div>

              <Button
                onClick={handleSendOTP}
                disabled={loading || !phoneNumber}
                className="w-full py-6 text-lg bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <MessageCircle className="w-5 h-5 mr-2" />
                )}
                Send Code via WhatsApp
              </Button>
            </div>
          )}

          {/* OTP Verification Step */}
          {authStep === 'otp' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full text-center text-2xl tracking-widest py-6 font-mono"
                  maxLength={6}
                />
              </div>

              {/* Remember Me */}
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-pink-500 focus:ring-pink-500"
                />
                <span className="text-gray-700">Remember me for 30 days</span>
              </label>

              <Button
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6}
                className="w-full py-6 text-lg bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-5 h-5 mr-2" />
                )}
                Verify & Sign In
              </Button>

              {/* Resend OTP */}
              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-gray-500">
                    Resend code in <span className="font-semibold text-pink-500">{countdown}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-pink-500 hover:text-pink-600 font-medium"
                  >
                    Didn't receive code? Resend
                  </button>
                )}
              </div>

              {/* Change Number */}
              <button
                onClick={() => setAuthStep('phone')}
                className="w-full text-center text-gray-500 hover:text-gray-700"
              >
                Change phone number
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
      <div className="absolute top-40 right-20 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 flex justify-between items-center">
          <div 
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => window.location.reload()}
            title="Refresh page"
          >
            <Heart className="w-8 h-8 text-pink-500 fill-pink-500" />
            <span className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">Heartlink</span>
          </div>
        </header>

        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-32">
          {/* YouTube Video Carousel - At the top */}
          <YouTubeCarousel />
          
          <div className="text-center space-y-8">
            <div className="inline-block">
              <div className="flex items-center justify-center space-x-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <span className="text-sm font-medium text-purple-700">GET THE MOST VOTES</span>
              </div>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                TRAVEL THE WORLD
              </span>
              <br />
              <span className="text-gray-800">AND FIND LOVE</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Experience the future of dating by matching and going on paid vacations.
              Connect with people who truly understand you.
            </p>

            {/* Login Buttons */}
            <div className="pt-8 space-y-4 max-w-sm mx-auto">
              {/* Google Login */}
              <Button
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                className="w-full px-10 py-6 text-lg font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full shadow-2xl hover:shadow-pink-500/50 transform hover:scale-105"
              >
                <Heart className="w-5 h-5 mr-2" />
                Continue with Google
              </Button>

              {/* Divider */}
              <div className="flex items-center space-x-4">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="text-gray-500 text-sm">or</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>

              {/* WhatsApp Login */}
              <Button
                data-testid="whatsapp-login-btn"
                onClick={() => setShowWhatsAppAuth(true)}
                className="w-full px-10 py-6 text-lg font-semibold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full shadow-2xl hover:shadow-green-500/50 transform hover:scale-105"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Continue with WhatsApp
              </Button>
            </div>
          </div>

          {/* Features */}
          <div className="mt-32 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-xl hover:shadow-2xl transform hover:-translate-y-2" style={{transition: 'box-shadow 0.3s ease, transform 0.3s ease'}}>
              <div className="w-14 h-14 bg-gradient-to-br from-pink-400 to-pink-600 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">PAID DATES</h3>
              <p className="text-gray-600">Match up on matrixshopping and we will pay for your dates locally and internationally.</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-xl hover:shadow-2xl transform hover:-translate-y-2" style={{transition: 'box-shadow 0.3s ease, transform 0.3s ease'}}>
              <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Real-Time Chat</h3>
              <p className="text-gray-600">Connect instantly with your matches through our seamless messaging platform.</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-xl hover:shadow-2xl transform hover:-translate-y-2" style={{transition: 'box-shadow 0.3s ease, transform 0.3s ease'}}>
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Safe & Secure</h3>
              <p className="text-gray-600">Your privacy and safety are our top priorities. All profiles are verified.</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Landing;
